import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';

import eq from 'webapp/helpers/eq';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineComponent from 'webapp/components/filter/line';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import MetricsUnusedListRowComponent from 'webapp/components/metrics/unused/list-row';
import type { UnusedItemModel } from 'webapp/components/metrics/unused/popup';

import FilterSortUnusedMetrics, {
	type MetricType,
} from 'webapp/models-custom/filter-sort/metric-unused';

interface Args {
	onChangeSelected: (items: UnusedItemModel[]) => void;
	onRecenter: () => void;
	initialMetricType?: MetricType;
}

export default class MetricsUnusedListComponent extends Component<Args> {
	@service declare logger: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;

	@tracked allUnusedItems: UnusedItemModel[] = [];
	@tracked selected: UnusedItemModel[] = [];
	@tracked submitInProgress = false;
	@tracked filterSort: FilterSortUnusedMetrics | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.getUnusedCustomQueriesAndScripts();
	}

	get displayedItems(): UnusedItemModel[] {
		return this.filterSort?.results ?? [];
	}

	get allSelected(): boolean {
		return (
			this.displayedItems.length > 0 && this.selected.length === this.displayedItems.length
		);
	}

	get noResults(): boolean {
		return !!this.filterSort?.noResults;
	}

	get isAnyFilterActive(): boolean {
		return !!this.filterSort?.isSearchActive || !!this.filterSort?.isAnyFilterActive;
	}

	getUnusedCustomQueriesAndScripts(): void {
		this.submitInProgress = true;

		this.requestService
			.api<APIData.Metrics.UnusedResponse>('GET', '/metrics/unused', null, {
				skipShowingErrorInfolayer: true,
			})
			.then((reply) => {
				const unusedItems: UnusedItemModel[] = [];

				reply.customQueries.forEach((id) => {
					const model = this.mmCustomQueriesService.findModel(String(id));
					if (model) {
						unusedItems.push(model);
					}
				});

				reply.scripts.forEach((id) => {
					const model = this.mmScriptsService.findModel(String(id));
					if (model) {
						unusedItems.push(model);
					}
				});

				reply.horizonMetrics?.forEach((id) => {
					const model = this.mmHorizonMetricsService.findModel(String(id));
					if (model) {
						unusedItems.push(model);
					}
				});

				this.allUnusedItems = unusedItems;
				this.initializeFilterSort();
				this.args.onRecenter();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	@action
	selectAll(value: boolean): void {
		const items = value ? [...this.displayedItems] : [];

		this.selected = items;
		this.args.onChangeSelected(items);
	}

	@action
	addToSelected(item: UnusedItemModel): void {
		if (!this.selected.includes(item)) {
			this.selected = [...this.selected, item];
		}
		this.args.onChangeSelected(this.selected);
	}

	@action
	removeFromSelected(item: UnusedItemModel): void {
		this.selected = this.selected.filter((i) => i !== item);
		this.args.onChangeSelected(this.selected);
	}

	@action
	resetFilters(): void {
		if (this.filterSort) {
			this.filterSort.resetFilters();
			this.filterSort.resetSearch();
		}
	}

	@action
	toggleSortOrder(): void {
		if (this.filterSort) {
			this.filterSort.sortOrder = this.filterSort.sortOrder === 'desc' ? 'asc' : 'desc';
		}
	}

	private initializeFilterSort(): void {
		this.filterSort = new FilterSortUnusedMetrics({
			getAllItems: () => this.allUnusedItems,
			afterFilter: () => {
				this.args.onRecenter();
			},
			initialTypeFilter: this.args.initialMetricType,
		});
	}
	<template>
		<div class="row">
			{{#if this.submitInProgress}}
				<BarLoaderComponent />
			{{else if this.filterSort}}
				{{#if this.allUnusedItems.length}}
					<FilterLineComponent @model={{this.filterSort}} />

					{{#if this.noResults}}
						<div class="no-results">
							{{{this.filterSort.safeNoResultsText}}}
							{{#if this.isAnyFilterActive}}
								<a role="button" {{on "click" this.resetFilters}}>Reset filters</a>
							{{/if}}
						</div>
					{{else}}
						<div class="table-wrapper">
							<table>
								<colgroup>
									<col width="35%" />
									<col width="20%" />
									<col width="25%" />
									<col width="20%" />
								</colgroup>
								<thead>
									<tr>
										<th class="c1">
											<FormCheckboxComponent
												@name="select_all"
												@selected={{this.allSelected}}
												@theme="red"
												@onChange={{this.selectAll}}
											/>
											<span>Metric</span>
										</th>
										<th class="c2">Data source(s)</th>
										<th class="c3">Created by</th>
										<th
											class="c4 sortable {{this.filterSort.sortOrder}}"
											role="button"
											{{on "click" this.toggleSortOrder}}
										>
											Created on
											<CommonUiIconComponent
												@name={{if
													(eq this.filterSort.sortOrder "asc")
													"ArrowUp"
													"ArrowDown"
												}}
												@size="xs"
											/>
										</th>
									</tr>
								</thead>
								<tbody>
									{{#each this.displayedItems as |item|}}
										<MetricsUnusedListRowComponent
											@model={{item}}
											@selected={{this.selected}}
											@onAddToSelected={{this.addToSelected}}
											@onRemoveFromSelected={{this.removeFromSelected}}
										/>
									{{/each}}
								</tbody>
							</table>
						</div>
					{{/if}}
				{{else}}
					<div class="no-results">
						<strong>No unused metrics...</strong>
					</div>
				{{/if}}
			{{/if}}
		</div>
	</template>
}
